import { IEventDataSource } from "@/types/interfaces";
import { logError } from "@lib/logger";
import { Game, PointCalculationDisplayNames, PointCalculationEnum } from "@/types/types";
import { CustomEventType } from "@/events/CustomEventRegistry";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

@customElement("moderate-event-table-row")
export class ModerateEventTableRow extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`

		.event-row {
		  display: flex;
		  align-items: center;
		  padding: 12px;
		  justify-content: space-between;
		  gap: 8px;
		}

		.event-row--has-been-played {
		  background: var(--color-mint);
		}

		.table-cell {
		  flex: 1;
		  display: flex;
		}
		.table-cell--aktionen {
		  flex: 1.5;
		}

		.event-row:hover {
		  background: #f8f9fa;
		}

		.event-row--highlight {
			animation: moderate-event-table-row-light-up 1s ease-out;
		}
		@keyframes moderate-event-table-row-light-up {
			from { background: var(--color-lemon); }
			to { background: transparent; }
		}
		@media (prefers-reduced-motion: reduce) {
			.event-row--highlight {
				animation: none;
			}
		}

		.actions {
		  display: flex;
		  gap: 8px;
		}

		basic-select {
			flex: 1;
		}
  `];

	@property({ type: Object }) game: Game;
	@property() eventId: string;
	@property({ type: Boolean }) isStartButtonDisabled: boolean = false;
	@property({ type: Array }) gamesForThisRoom: Game[] = [];
	@property({ type: String }) rowIndex: string = "";
	@property({ type: Boolean }) hasBeenPlayed: boolean = false;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	@property({ type: Boolean }) highlight: boolean = false;

	constructor(game: Game, eventId: string, isStartButtonDisabled: boolean, gamesForThisRoom: Game[], eventDataSource: IEventDataSource) {
		super();
		this.game = game;
		this.eventId = eventId;
		this.isStartButtonDisabled = isStartButtonDisabled;
		this.gamesForThisRoom = gamesForThisRoom;
		this.eventDataSource = eventDataSource;
	}

	private _handleResetGamePoints = async () => {
		if (!this.hasBeenPlayed) {
			alert('Dieses Spiel hat noch keine Punkte.');
			return;
		}
		const confirmed = window.confirm('Punkte für dieses Spiel wirklich zurücksetzen?');
		if (!confirmed) return;
		try {
			await this.eventDataSource.deleteGamePointsForGame(this.eventId, this.game.id);
			this.dispatchEvent(new CustomEvent(CustomEventType.POINTS_RESET, { bubbles: true, composed: true }));
		} catch (err) {
			logError('Failed to reset game points', err);
			alert('Fehler beim Zurücksetzen der Punkte.');
		}
	};

	private _handleStartClick(): void {
		window.history.pushState({}, '', `/host/moderate-event/${this.eventId}/${this.game.id}`);
		window.dispatchEvent(new PopStateEvent('popstate'));
	}

	private _handleLocalSelectionChange(e: CustomEvent) {
		// bubble-up handlers are handled elsewhere; keep selection-change emitted by basic-select
	}

	private _createGameOptions() {
		return this.gamesForThisRoom.map(g => ({ label: g.name, value: g.id }));
	}

	render() {
		return html`
		<div class="event-row ${this.hasBeenPlayed ? 'event-row--has-been-played' : ''} ${this.highlight ? 'event-row--highlight' : ''}">
			<div class="table-cell">
				<basic-select
					.additionalCustomEventIdentifier="${this.game.default_room}"
					.rowIndex="${this.rowIndex}"
					.value="${this.game.id}"
					.options="${this._createGameOptions()}"
					@selection-change=${this._handleLocalSelectionChange}
				></basic-select>
			</div>
			<div class="table-cell">
				<div class="text-body--small">${PointCalculationDisplayNames[this.game.point_calculation as PointCalculationEnum]}</div>
			</div>
			<div class="table-cell table-cell--aktionen">
				<div class="actions">
					<success-button @click="${this._handleStartClick}" ?disabled="${this.isStartButtonDisabled}">Start</success-button>
					<alert-button @click="${this._handleResetGamePoints}">Punkte zurücksetzen</alert-button>
				</div>
			</div>
		</div>
		`;
	}
}

export default ModerateEventTableRow;